import { useQuery } from "@tanstack/react-query";
import { tenantApi } from "@/lib/api/client";
import { DOORBELL_COMPANY_SETTINGS_KEY, doorbellCallsApi } from "@/lib/api/doorbell-calls";
import { useApprovalSettings } from "@/hooks/use-booking-approvals";
import { useSlackWorkspaces } from "@/hooks/use-slack-workspaces";
import { calendarFeedsStatus, openingHoursStatus, type SettingsAreaKey } from "@/lib/settings-areas";

export interface AreaStatus {
  label: string;
  /** "green" = set up / on; "gray" = not (yet). */
  color: "green" | "gray";
}

/**
 * The small status line under each Settings tile, from the queries the areas'
 * own cards already make (same keys and shapes, so opening an area is instant).
 * Admin-only reads are skipped for members. An area with nothing cheap to say
 * gets no status.
 */
export function useSettingsAreaStatus(isAdmin: boolean): {
  statuses: Partial<Record<SettingsAreaKey, AreaStatus>>;
  approvalEnabled: boolean | undefined;
} {
  const { data: workspaces, isSuccess: slackLoaded } = useSlackWorkspaces();
  const { data: approval } = useApprovalSettings();
  // Same key and shape as OpeningHoursEditor (the whole Axios response).
  const { data: schedule } = useQuery({
    queryKey: ["weekly-schedule"],
    queryFn: () => tenantApi.getWeeklySchedule(),
    enabled: isAdmin,
  });
  const { data: doorbell } = useQuery({
    queryKey: DOORBELL_COMPANY_SETTINGS_KEY,
    queryFn: () => doorbellCallsApi.getCompanySettings().then((r) => r.data),
    enabled: isAdmin,
    retry: false,
    staleTime: 30_000,
  });
  // Same key and shape as CalendarFeedsCard.
  const { data: feeds } = useQuery({
    queryKey: ["tenant-calendar-feeds"],
    queryFn: () => tenantApi.listCalendarFeeds().then((r) => r.data),
  });

  const statuses: Partial<Record<SettingsAreaKey, AreaStatus>> = {};
  if (slackLoaded) {
    statuses.slack = workspaces && workspaces.length > 0
      ? { label: "Connected", color: "green" }
      : { label: "Not connected", color: "gray" };
  }
  const hours = openingHoursStatus(schedule?.data);
  if (hours) statuses["opening-hours"] = { label: hours, color: hours === "Not set" ? "gray" : "green" };
  if (approval?.enabled) {
    statuses["booking-approval"] = approval.requires_approval
      ? { label: "Approval on", color: "green" }
      : { label: "Approval off", color: "gray" };
  }
  if (doorbell) {
    statuses.doorbell = doorbell.doorbell_calls_enabled
      ? { label: "Live calls on", color: "green" }
      : { label: "Live calls off", color: "gray" };
  }
  const feedsLabel = calendarFeedsStatus(feeds?.length);
  if (feedsLabel) statuses["calendar-feeds"] = { label: feedsLabel, color: feeds && feeds.length > 0 ? "green" : "gray" };

  return { statuses, approvalEnabled: approval ? approval.enabled : undefined };
}
